import React, { useState } from 'react';

// Types
import {
  FormTypeType,
  AdjectivesForPropertyQuestionType,
  ReviewFormTemplateType,
} from 'Shared/types/ReviewFormTemplate.type';

// Hooks
import { useQuestion } from './use-question';

// Components
import { WpButton } from 'Wpds/components/button';
import QuestionWrapper from './QuestionWrapper';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpListSelect } from 'Wpds/components/list';

type Props = {
  question: AdjectivesForPropertyQuestionType;
  onChange: Function;
  handleDeleteQuestion: Function;
  handleDuplicateQuestion: Function;
  activeQuestionKey: string;
  setActiveQuestionKey: Function;
  handleMoveQuestion: Function;
  reviewFormType: FormTypeType;
  reviewFormTemplates: ReviewFormTemplateType[];
};

type AdjectiveOptionProps = {
  label: string;
  number: number;
  adjectiveType: string;
  handleUpdateAdjectives: Function;
  handleDeleteAdjective?: Function;
};

const AdjectiveOption = ({
  number,
  adjectiveType,
  label,
  handleUpdateAdjectives,
  handleDeleteAdjective,
}: AdjectiveOptionProps) => {
  const [labelValue, setLabelValue] = useState(label);

  const handleUpdateLabel = value => {
    setLabelValue(value);
    handleUpdateAdjectives({ number, label: value, adjectiveType: adjectiveType });
  };

  const handleDeleteAdjectiveClick = () => {
    handleDeleteAdjective({ number, adjectiveType: adjectiveType });
  };

  const baseClassName = 'RatingQuestion';

  return (
    <div className={`${baseClassName}__values__value`}>
      <div className={`${baseClassName}__values__value__label`}>
        <WpTextField
          id="label-input"
          label="label"
          value={labelValue}
          labelHidden
          placeholder="Label"
          onChange={handleUpdateLabel}
          max={30}
        />
      </div>

      {handleDeleteAdjective && (
        <div className={`${baseClassName}__values__value__actions`} onClick={handleDeleteAdjectiveClick}>
          <WpButton variant="tertiary">
            <WpIcon name="trash" />
          </WpButton>
        </div>
      )}
    </div>
  );
};

const AdjectivesForPropertyQuestion = ({
  question,
  onChange,
  handleDeleteQuestion,
  activeQuestionKey,
  setActiveQuestionKey,
  handleDuplicateQuestion,
  handleMoveQuestion,
  reviewFormType,
  reviewFormTemplates,
}: Props) => {
  const baseClassName = 'AdjectivesForPropertyQuestion';
  const { handleUpdateQuestion } = useQuestion({ question, onChange });

  const adjectives = question.adjectives;
  const [property, setProperty] = useState(question.property);

  const handleUpdateProperty = value => {
    setProperty(value);
    handleUpdateQuestion({ property: value });
  };

  const isActive = activeQuestionKey === question.key;

  const handleDeleteAdjective = ({ adjectiveType, number }) => {
    const updatedAdjectives = Object.fromEntries(
      Object.entries(adjectives).map(adjectivesEntry => {
        const adjectiveTypeInner = adjectivesEntry[0];
        const adjectivesInner = adjectivesEntry[1];
        const newAdjectives =
          adjectiveTypeInner === adjectiveType ? adjectivesInner.filter((_, idx) => idx !== number) : adjectivesInner;
        return [adjectiveTypeInner, newAdjectives];
      })
    );
    handleUpdateQuestion({
      adjectives: { positive: updatedAdjectives.positive, negative: updatedAdjectives.negative },
    });
  };

  const handleUpdateAdjectives = ({ adjectiveType, number, label }) => {
    const updatedAdjectives = Object.fromEntries(
      Object.entries(adjectives).map(adjectivesEntry => {
        const adjectiveTypeInner = adjectivesEntry[0];
        const adjectivesInner = adjectivesEntry[1];
        const newAdjectives =
          adjectiveTypeInner === adjectiveType
            ? adjectivesInner.map((a, idx) => (idx === number ? label : a))
            : adjectivesInner;
        return [adjectiveTypeInner, newAdjectives];
      })
    );
    handleUpdateQuestion({
      adjectives: { positive: updatedAdjectives.positive, negative: updatedAdjectives.negative },
    });
  };

  const handleAddAdjective = adjectiveType => {
    const updatedAdjectives = Object.fromEntries(
      Object.entries(adjectives).map(adjectivesEntry => {
        const adjectiveTypeInner = adjectivesEntry[0];
        const adjectivesInner = adjectivesEntry[1];
        const newAdjectives = adjectiveTypeInner === adjectiveType ? [...adjectivesInner, ''] : adjectivesInner;
        return [adjectiveTypeInner, newAdjectives];
      })
    );
    handleUpdateQuestion({
      adjectives: { positive: updatedAdjectives.positive, negative: updatedAdjectives.negative },
    });
  };

  const capitalizeWord = string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <QuestionWrapper
      question={question}
      onChange={handleUpdateQuestion}
      handleDeleteQuestion={handleDeleteQuestion}
      activeQuestionKey={activeQuestionKey}
      setActiveQuestionKey={setActiveQuestionKey}
      handleDuplicateQuestion={handleDuplicateQuestion}
      handleMoveQuestion={handleMoveQuestion}
      reviewFormType={reviewFormType}
      reviewFormTemplates={reviewFormTemplates}
    >
      {isActive ? (
        <div className={baseClassName}>
          <WpTextField
            id={`adj_property_${question.key}`}
            label="Property"
            value={property}
            placeholder="Property to find adjectives for"
            onChange={handleUpdateProperty}
            required
          />
          {['positive', 'negative'].map(adjectiveType => (
            <div key={`${adjectiveType}_adjective_question_container`} className={`${baseClassName}__values`}>
              <div className="mt-2 mb-1">{capitalizeWord(adjectiveType)} adjectives</div>
              {adjectives[adjectiveType].map((value, valueIndex) => (
                <AdjectiveOption
                  number={valueIndex}
                  label={value}
                  adjectiveType={adjectiveType}
                  key={`${valueIndex}_${adjectiveType}_adjective_question`}
                  handleUpdateAdjectives={handleUpdateAdjectives}
                  handleDeleteAdjective={valueIndex > 0 && handleDeleteAdjective}
                />
              ))}
              {adjectives[adjectiveType].length < 10 && (
                <WpButton variant="tertiary" onClick={() => handleAddAdjective(adjectiveType)}>
                  <WpIcon name="plus" /> Add {adjectiveType} adjective
                </WpButton>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={baseClassName}>
          <div className={`${baseClassName}__not-active`}>
            <p className={`${baseClassName}__question`}>
              <span className={`${baseClassName}__question__unbold`}>Select up to 2</span>
            </p>
            <div className={`${baseClassName}__adjective-list`}>
              <WpListSelect
                id={`${question.key}-list-example`}
                selectedOptions={[]}
                options={[...adjectives.positive, ...adjectives.negative]}
                direction="horizontal"
                max={1}
                showIcon={false}
                disableOnMax={false}
              />
            </div>
            <p className={`${baseClassName}__question`}>Why did you choose those adjectives?</p>
            <WpTextField
              id="rating-explanation-placeholder"
              label="explanation"
              placeholder="Short Answer"
              labelHidden
              readOnly
              multiline={2}
            />
          </div>
        </div>
      )}
    </QuestionWrapper>
  );
};

export default AdjectivesForPropertyQuestion;
